import React, { useEffect, useMemo, useRef, useState } from "react";
import Joyride, { EVENTS, STATUS } from "react-joyride";
import { fabric } from "fabric";
import { jsPDF } from "jspdf";
import ReactDOM from "react-dom";
import QRCode from "easyqrcodejs";
import JsBarcode from "jsbarcode"; // Import jsbarcode
import "./BadgeEditor.css";
import QRCodeDialog from "./QRCodeDialog";
import { FieldNames, PDFControls } from "./PDFControls";
import { Canvas } from "./Canvas";
import { Controls } from "./Controls";
import { TopControls } from "./TopControls";
import Modal from "@mui/material/Modal";
import TemplateCards from "./TemplateCards";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

import * as XLSX from "xlsx";
import Header from "./Header";
import Footer from "./Footer";
import NewFieldDialog from "./NewFieldDialog";
import {
  LOGO_IMAGE_DATA_URL,
  HELP_IMPROVE_DIALOG_SEEN_KEY,
} from "./constants";
import SuccessDialog from "./SuccessDialog";
import HelpImproveDialog from "./HelpImproveDialog";
import { calculateCanvasSize } from "../helpers/calculateCanvasSize";
import PrintDialog from "./PrintDialog";
import {
  tourSteps,
  hasSeenTour,
  markTourSeen,
} from "../helpers/tour";
import {
  fitTextElementToCanvasWidth,
  scaleTextElementFontSize,
} from "../helpers/textElementSizing";
import SizeDialog from "./SizeDialog";
import ImportSpreadsheetDialog, {
  PRESET_FIELDS,
  REIMPORT_MODES,
} from "./ImportSpreadsheetDialog";
import { useAuth } from "../auth/AuthContext";
import {
  deleteEventImage,
  parseEventAssetPath,
  uploadEventImage,
} from "../events/storageService";
import { extractBadgeTemplate, isCodeType } from "../helpers/badgeTemplate";
import { renderBadgeTemplateObjects } from "../helpers/renderBadgeTemplate";
import { calculateTemplateForSize } from "../helpers/templateSizing";
import { getBadgeSizeTrackingValue, trackEvent } from "../helpers/trackEvent";
import paymentPricing from "../payments/paymentPricing";
import { isPaymentsFeatureEnabled } from "../payments/paymentFeature";
import {
  beginBadgePurchase,
  hasPaidBadgeSets,
  listPaidBadgeCredits,
  listPaidBadgeSets,
  storePendingGeneration,
} from "../payments/paymentsService";
import PurchasedBadgesDialog from "../payments/PurchasedBadgesDialog";
import {
  HiddenPurchasedFieldsDialog,
  PurchasedFieldsMappingDialog,
} from "../payments/PurchasedBadgeRegenerationDialogs";
import SuccessfulPaymentDialog from "../payments/SuccessfulPaymentDialog";
import regenerationCompatibility from "../payments/regenerationCompatibility";

// Add this at the top where other window properties are initialized
window.guidingLines = [];

const NEXT_ELEMENT_SHIFT = 20;
const EVENT_HYDRATION_SETTLE_DELAY_MS = 140;
const { calculatePaymentQuote, spreadsheetDataToAttendees } = paymentPricing;
const {
  getPurchasedFieldMapping,
  getRegenerationCompatibility,
  mapPurchasedAttendeesToCurrentFields,
} = regenerationCompatibility;

const getEventChangeSignature = (payload) =>
  JSON.stringify({
    badgeSize: payload?.badgeSize ?? null,
    badgeTemplate: payload?.badgeTemplate ?? null,
    importedSpreadsheetData: payload?.importedSpreadsheetData ?? null,
  });


const buildImportedSpreadsheetDataFromEvent = (eventData) => {
  if (eventData?.spreadsheetData) {
    return eventData.spreadsheetData;
  }

  return null;
};

const normalizeTemplateFieldValue = (value) =>
  String(value ?? "").replace(/ /g, "\u00A0");

const getImportedSpreadsheetFieldValues = (importedSpreadsheetData) => {
  if (!importedSpreadsheetData) {
    return {};
  }

  const { mapping = {}, fileData = {} } = importedSpreadsheetData;
  const firstRow = fileData.data?.[0] || [];
  const headers = fileData.headers || [];

  return Object.entries(mapping).reduce((fieldValues, [header, fieldName]) => {
    const columnIndex = headers.indexOf(header);
    if (columnIndex !== -1) {
      const value = firstRow[columnIndex];
      fieldValues[fieldName] = value != null ? String(value).trim() : "";
    }
    return fieldValues;
  }, {});
};

const getUniqueValues = (values) =>
  Array.from(new Set(values.filter(Boolean)));

const getImportedSpreadsheetFieldNames = (importedSpreadsheetData) =>
  getUniqueValues(Object.values(importedSpreadsheetData?.mapping || {}));

const getImportedSpreadsheetRowsByFieldName = (
  importedSpreadsheetData,
  fieldNames
) => {
  if (!importedSpreadsheetData) {
    return [];
  }

  const { mapping = {}, fileData = {} } = importedSpreadsheetData;
  const headers = fileData.headers || [];
  const dataRows = fileData.data || [];
  const fieldToColumnIndex = Object.entries(mapping).reduce(
    (acc, [header, fieldName]) => {
      if (acc[fieldName] !== undefined) {
        return acc;
      }

      const columnIndex = headers.indexOf(header);
      if (columnIndex !== -1) {
        acc[fieldName] = columnIndex;
      }

      return acc;
    },
    {}
  );

  return dataRows.map((row) =>
    fieldNames.map((fieldName) => {
      const columnIndex = fieldToColumnIndex[fieldName];
      const value = columnIndex === undefined ? "" : row[columnIndex];
      return value == null ? "" : value;
    })
  );
};

const createIdentityMapping = (fieldNames) =>
  fieldNames.reduce((mapping, fieldName) => {
    mapping[fieldName] = fieldName;
    return mapping;
  }, {});

const mergeImportedSpreadsheetData = (previousData, nextData) => {
  const previousFieldNames = getImportedSpreadsheetFieldNames(previousData);
  const nextFieldNames = getImportedSpreadsheetFieldNames(nextData);
  const mergedFieldNames = getUniqueValues([
    ...previousFieldNames,
    ...nextFieldNames,
  ]);

  return {
    ...nextData,
    eventName: previousData?.eventName || nextData?.eventName,
    mapping: createIdentityMapping(mergedFieldNames),
    fileData: {
      headers: mergedFieldNames,
      data: [
        ...getImportedSpreadsheetRowsByFieldName(previousData, mergedFieldNames),
        ...getImportedSpreadsheetRowsByFieldName(nextData, mergedFieldNames),
      ],
    },
  };
};

const getFieldsStateForImportedSpreadsheetUpdate = (
  currentFields,
  importedSpreadsheetData,
  { keepUnmappedFields = true } = {}
) => {
  const mappedFieldNames = getImportedSpreadsheetFieldNames(importedSpreadsheetData);
  const fieldValues = getImportedSpreadsheetFieldValues(importedSpreadsheetData);
  const nextFields = Object.entries(currentFields).reduce(
    (acc, [label, field]) => {
      if (!keepUnmappedFields && !mappedFieldNames.includes(label)) {
        return acc;
      }

      acc[label] = {
        ...field,
        value: normalizeTemplateFieldValue(
          mappedFieldNames.includes(label) ? fieldValues[label] || "" : ""
        ),
        focused: false,
      };
      return acc;
    },
    {}
  );

  mappedFieldNames.forEach((fieldName) => {
    if (nextFields[fieldName]) {
      nextFields[fieldName] = {
        ...nextFields[fieldName],
        type: nextFields[fieldName].type || PRESET_FIELDS[fieldName]?.type || "text",
      };
      return;
    }

    nextFields[fieldName] = {
      value: normalizeTemplateFieldValue(fieldValues[fieldName] || ""),
      type: PRESET_FIELDS[fieldName]?.type || "text",
      enabled: false,
      element: null,
    };
  });

  return nextFields;
};

const getTemplateFieldsForImportedSpreadsheetData = (
  template,
  importedSpreadsheetData
) => {
  const templateFields = template?.fields || [];

  if (!importedSpreadsheetData) {
    return templateFields.map(({ element, value, ...field }) => ({
      ...field,
      value: normalizeTemplateFieldValue(value),
    }));
  }

  const { mapping = {} } = importedSpreadsheetData;
  const fieldValues = getImportedSpreadsheetFieldValues(importedSpreadsheetData);
  const mappedFieldNames = Object.values(mapping);
  const nextFields = [];

  templateFields.forEach(({ element, label, value, enabled = true, ...rest }) => {
    if (mappedFieldNames.includes(label)) {
      nextFields.push({
        label,
        value: normalizeTemplateFieldValue(fieldValues[label] || value || ""),
        ...rest,
        enabled,
      });
    }
  });

  mappedFieldNames.forEach((fieldName) => {
    if (nextFields.some(({ label }) => label === fieldName)) {
      return;
    }

    nextFields.push({
      label: fieldName,
      value: normalizeTemplateFieldValue(fieldValues[fieldName] || ""),
      type: PRESET_FIELDS[fieldName]?.type || "text",
      enabled: false,
    });
  });

  return nextFields;
};

const getFieldValuesFromFields = (fields) =>
  Object.entries(fields || {}).reduce((fieldValues, [label, field]) => {
    fieldValues[label] = field?.value ?? "";
    return fieldValues;
  }, {});

const getTemplateFieldsForExistingEventFields = (
  template,
  importedSpreadsheetData,
  existingFields
) => {
  const templateFields = template?.fields || [];
  const existingFieldNames = Object.keys(existingFields || {});
  const mappedFieldNames = getUniqueValues([
    ...existingFieldNames,
    ...getImportedSpreadsheetFieldNames(importedSpreadsheetData),
  ]);
  const fieldValues = {
    ...getFieldValuesFromFields(existingFields),
    ...getImportedSpreadsheetFieldValues(importedSpreadsheetData),
  };
  const nextFields = [];

  templateFields.forEach(({ element, label, value, enabled = true, ...rest }) => {
    if (mappedFieldNames.includes(label)) {
      nextFields.push({
        label,
        value: normalizeTemplateFieldValue(fieldValues[label] || value || ""),
        ...rest,
        enabled,
      });
    }
  });

  mappedFieldNames.forEach((fieldName) => {
    if (nextFields.some(({ label }) => label === fieldName)) {
      return;
    }

    const existingField = existingFields?.[fieldName] || {};
    nextFields.push({
      label: fieldName,
      value: normalizeTemplateFieldValue(fieldValues[fieldName] || ""),
      type: existingField.type || PRESET_FIELDS[fieldName]?.type || "text",
      enabled: false,
    });
  });

  return nextFields;
};

const getFieldsStateFromTemplateFields = (templateFields) =>
  templateFields.reduce((acc, { element, label, value, ...rest }) => {
    acc[label] = {
      value: normalizeTemplateFieldValue(value),
      ...rest,
      element: null,
    };
    return acc;
  }, {});

const getTemplateForImportedSpreadsheetData = (template, importedSpreadsheetData) => ({
  ...template,
  fields: getTemplateFieldsForImportedSpreadsheetData(
    template,
    importedSpreadsheetData
  ),
});

const DEFAULT_FIELDS = [
  {
    label: "First name",
    value: "Denis",
    left: 50,
    top: 50,
    width: 500,
    fontSize: 40,
  },
  {
    label: "Last name",
    value: "Rasloc",
    left: 50,
    top: 120,
    width: 500,
    fontSize: 60,
  },
  {
    label: "Company name",
    value: "Microsoft",
    left: 50,
    top: 300,
    width: 500,
    fontSize: 50,
  },
];

const BadgeEditor = ({
  mode = "standalone",
  eventData = null,
  initialConfirmedPurchase = null,
  initialHasPurchasedBadgeSets = null,
  isDemoMode = false,
  headerProps = {},
  onEventCreate,
  onEventChange,
  onEventNameValidate,
  readOnly = false,
} = {}) => {
  const { session, user } = useAuth();
  const initialConfirmedPayment = initialConfirmedPurchase?.payment || null;
  const initialPaidGeneration =
    initialConfirmedPayment && initialConfirmedPurchase?.savedGeneration?.format
      ? {
          attendees: spreadsheetDataToAttendees(
            initialConfirmedPayment.spreadsheet_data,
            initialConfirmedPayment.field_mapping
          ),
          format: initialConfirmedPurchase.savedGeneration.format,
        }
      : null;
  const [paymentsFeatureEnabled] = useState(isPaymentsFeatureEnabled);
  const paymentsEnabled =
    mode === "event" && !isDemoMode && !readOnly && paymentsFeatureEnabled;
  const canvasRef = useRef(null);
  const [openQr, setOpenQr] = useState(false);
  const [isNewFieldDialogOpen, setIsNewFieldDialogOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [openImage, setOpenImage] = useState(false);
  const [selectedObject, setSelectedObject] = useState({ element: null });
  const [color, setColor] = useState("#000000"); // Initial color blue
  const [currentFont, setCurrentFont] = useState("");
  const [currentFontSize, setCurrentFontSize] = useState(null);
  const [removeButtonPosition, setRemoveButtonPosition] = useState({
    top: 0,
    left: 0,
  });
  const [fieldIdPosition, setFieldIdPosition] = useState({
    top: 0,
    left: 0,
  });
  const [elementSizePosition, setElementSizePosition] = useState({
    top: 0,
    left: 0,
  });
  const [elementsCount, setElementsCount] = useState({
    text: 0,
    rectangle: 0,
    qrCode: 0,
    image: 0,
  });

  const [size, setSize] = useState(eventData?.badgeSize ?? null);

  const [importedSpreadsheetData, setImportedSpreadsheetDataState] = useState(
    buildImportedSpreadsheetDataFromEvent(eventData)
  );
  const [draftEventName, setDraftEventName] = useState("");
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [hasLoadedEvent, setHasLoadedEvent] = useState(!eventData);
  const saveTimerRef = useRef(null);
  const onEventChangeRef = useRef(onEventChange);
  const sizeRef = useRef(size);
  const fieldsRef = useRef({});
  const shouldSkipNextFieldsSaveRef = useRef(false);
  const previousCanvasSideEffectsSuppressedRef = useRef(false);
  const importedSpreadsheetDataRef = useRef(importedSpreadsheetData);
  const hasLoadedEventRef = useRef(hasLoadedEvent);
  const isHydratingEventRef = useRef(false);
  const loadedEventPayloadSignatureRef = useRef("");
  const hydrationSettleTimerRef = useRef(null);
  const canvasSideEffectsSuppressedRef = useRef(false);

  const [fields, setFields] = useState(
    DEFAULT_FIELDS.reduce((acc, { label, value, ...rest }) => {
      acc[label] = {
        value,
        element: null,
      };
      return acc;
    }, {})
  );
  const existingImportFieldOptions = useMemo(
    () => getUniqueValues([
      ...Object.keys(fields),
      ...getImportedSpreadsheetFieldNames(importedSpreadsheetData),
    ]),
    [fields, importedSpreadsheetData]
  );

  const updateFields = (nextFields, { suppressTemplateSave = false } = {}) => {
    if (suppressTemplateSave) {
      shouldSkipNextFieldsSaveRef.current = true;
      previousCanvasSideEffectsSuppressedRef.current =
        canvasSideEffectsSuppressedRef.current;
      canvasSideEffectsSuppressedRef.current = true;
    }

    setFields(nextFields);
  };

  const [horizontalLine, setHorizontalLine] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(!eventData);

  const [printDialogState, setPrintDialogState] = useState({ type: 'closed' });
  const [paymentQuote, setPaymentQuote] = useState(null);
  const [isPaymentQuoteLoading, setIsPaymentQuoteLoading] = useState(false);
  const [isPurchasedBadgesDialogOpen, setIsPurchasedBadgesDialogOpen] =
    useState(false);
  const [hasPurchasedBadgeSets, setHasPurchasedBadgeSets] = useState(
    initialHasPurchasedBadgeSets === true || Boolean(initialConfirmedPayment)
  );
  const [hiddenPurchasedFieldNames, setHiddenPurchasedFieldNames] = useState([]);
  const [regenerationMappingState, setRegenerationMappingState] =
    useState(null);
  const [isPaymentSuccessDialogOpen, setIsPaymentSuccessDialogOpen] =
    useState(Boolean(initialPaidGeneration));
  const [successfulPaymentSummary, setSuccessfulPaymentSummary] = useState({
    amountCents: initialConfirmedPayment?.amount_cents || 0,
    badgeCreditPurchasedCount:
      initialConfirmedPayment?.badge_credit_purchased_count || 0,
    badgeCreditUsedCount:
      initialConfirmedPayment?.badge_credit_used_count || 0,
  });
  const [pendingPaidGeneration, setPendingPaidGeneration] = useState(
    initialPaidGeneration
  );
  const [isReimportDialogOpen, setIsReimportDialogOpen] = useState(false);
  const [isChangeSizeDialogOpen, setIsChangeSizeDialogOpen] = useState(false);
  const [isChangeTemplateMode, setIsChangeTemplateMode] = useState(false);
  const [attendeeSelectionResetKey, setAttendeeSelectionResetKey] = useState(0);
  const [isStaticSuccessDialogOpen, setIsStaticSuccessDialogOpen] =
    useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [isHelpImproveOpen, setIsHelpImproveOpen] = useState(false);
  const [isTourActive, setIsTourActive] =
    useState(false);
  const [runFieldsTour, setRunFieldsTour] = useState(false);
  const [fieldsJoyrideKey, setFieldsJoyrideKey] = useState(0);

  const openHelpImproveDialogIfNeeded = () => {
    try {
      if (!localStorage.getItem(HELP_IMPROVE_DIALOG_SEEN_KEY)) {
        setIsHelpImproveOpen(true);
      }
    } catch (_) {
      setIsHelpImproveOpen(true);
    }
  };

  const getCurrentEventPayload = () => {
    const currentSize = sizeRef.current;

    if (!currentSize || !window.canvas) {
      return null;
    }

    return {
      badgeSize: currentSize,
      badgeTemplate: extractBadgeTemplate(window.canvas, fieldsRef.current),
      importedSpreadsheetData: importedSpreadsheetDataRef.current,
    };
  };

  const captureLoadedEventPayloadSignature = () => {
    const payload = getCurrentEventPayload();

    if (payload) {
      loadedEventPayloadSignatureRef.current = getEventChangeSignature(payload);
    }
  };

  const finishEventHydration = () => {
    clearTimeout(hydrationSettleTimerRef.current);

    hydrationSettleTimerRef.current = setTimeout(() => {
      captureLoadedEventPayloadSignature();
      isHydratingEventRef.current = false;
    }, EVENT_HYDRATION_SETTLE_DELAY_MS);
  };

  const emitEventChange = () => {
    const changeHandler = onEventChangeRef.current;

    if (isDemoMode || !changeHandler) {
      return;
    }

    const payload = getCurrentEventPayload();

    if (!payload) {
      return;
    }

    const payloadSignature = getEventChangeSignature(payload);

    if (loadedEventPayloadSignatureRef.current === payloadSignature) {
      return;
    }

    loadedEventPayloadSignatureRef.current = payloadSignature;
    changeHandler(payload);
  };

  const flushPendingEventSave = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    if (isHydratingEventRef.current || !hasLoadedEventRef.current) {
      return;
    }

    emitEventChange();
  };

  const scheduleEventSave = () => {
    if (
      readOnly ||
      isDemoMode ||
      isHydratingEventRef.current ||
      !hasLoadedEventRef.current ||
      !onEventChangeRef.current
    ) {
      return;
    }

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      emitEventChange();
    }, 700);
  };

  const runWithoutCanvasSideEffects = (callback) => {
    const wasSuppressed = canvasSideEffectsSuppressedRef.current;
    canvasSideEffectsSuppressedRef.current = true;
    try {
      return callback();
    } finally {
      canvasSideEffectsSuppressedRef.current = wasSuppressed;
    }
  };

  const getEventAssetPath = (object) =>
    object?.eventAssetPath || parseEventAssetPath(object?.imageUrl);

  const deleteCanvasImageAsset = (object) => {
    const filePath = getEventAssetPath(object);
    if (!filePath || isDemoMode) {
      return;
    }

    deleteEventImage({ filePath }).catch((error) => {
      console.error("Could not delete image from storage.", error);
      toast.error("Image was removed, but could not be deleted from storage");
    });
  };

  const setImportedSpreadsheetData = (data) => {
    importedSpreadsheetDataRef.current = data;
    setImportedSpreadsheetDataState(data);
    if (data?.eventName) {
      setDraftEventName(data.eventName);
    }
    scheduleEventSave();
  };

  const handleReimportContinue = (data) => {
    const { importMode, ...spreadsheetData } = data;
    const previousSpreadsheetData = importedSpreadsheetDataRef.current;
    const nextSpreadsheetData =
      importMode === REIMPORT_MODES.APPEND
        ? mergeImportedSpreadsheetData(previousSpreadsheetData, spreadsheetData)
        : spreadsheetData;
    const eventName =
      previousSpreadsheetData?.eventName ||
      draftEventName ||
      eventData?.name ||
      nextSpreadsheetData.eventName;
    const dataWithEventName = eventName
      ? { ...nextSpreadsheetData, eventName }
      : nextSpreadsheetData;
    const shouldKeepUnmappedFields = importMode === REIMPORT_MODES.APPEND;
    const nextMappedFieldNames = getImportedSpreadsheetFieldNames(dataWithEventName);

    if (!shouldKeepUnmappedFields) {
      Object.keys(fieldsRef.current).forEach((label) => {
        if (!nextMappedFieldNames.includes(label)) {
          removeFieldElement(label);
        }
      });
    }

    setImportedSpreadsheetData(dataWithEventName);
    updateFields(
      getFieldsStateForImportedSpreadsheetUpdate(
        fieldsRef.current,
        dataWithEventName,
        { keepUnmappedFields: shouldKeepUnmappedFields }
      )
    );
    setAttendeeSelectionResetKey((key) => key + 1);
    setIsReimportDialogOpen(false);
    trackEvent({
      action: "attendees_reimported",
      data: {
        mode: importMode,
        attendeesCount: dataWithEventName.fileData?.data?.length || 0,
      },
    });

    return dataWithEventName;
  };

  useEffect(() => {
    onEventChangeRef.current = onEventChange;
  }, [onEventChange]);

  useEffect(() => {
    sizeRef.current = size;
    scheduleEventSave();
  }, [size]);

  useEffect(() => {
    fieldsRef.current = fields;
    if (shouldSkipNextFieldsSaveRef.current) {
      return;
    }

    scheduleEventSave();
  }, [fields]);

  useEffect(() => {
    importedSpreadsheetDataRef.current = importedSpreadsheetData;
  }, [importedSpreadsheetData]);

  useEffect(() => {
    let isStale = false;

    if (initialHasPurchasedBadgeSets !== null) {
      setHasPurchasedBadgeSets(initialHasPurchasedBadgeSets === true);
      return undefined;
    }

    if (!paymentsEnabled || !eventData?.id || !user?.id) {
      setHasPurchasedBadgeSets(false);
      return undefined;
    }

    hasPaidBadgeSets(eventData.id, user.id)
      .then((hasPayments) => {
        if (!isStale) setHasPurchasedBadgeSets(hasPayments);
      })
      .catch(() => {
        if (!isStale) setHasPurchasedBadgeSets(false);
      });

    return () => {
      isStale = true;
    };
  }, [
    eventData?.id,
    initialHasPurchasedBadgeSets,
    paymentsEnabled,
    user?.id,
  ]);

  useEffect(() => {
    let isStale = false;

    if (!paymentsEnabled || printDialogState.type === "closed") {
      setPaymentQuote(null);
      setIsPaymentQuoteLoading(false);
      return undefined;
    }

    const spreadsheetData =
      printDialogState.purchaseSpreadsheetData || importedSpreadsheetData;
    const fieldMapping =
      printDialogState.purchaseFieldMapping || spreadsheetData?.mapping || {};

    if (!spreadsheetData || !eventData?.id || !user?.id) {
      setPaymentQuote(null);
      setIsPaymentQuoteLoading(false);
      return undefined;
    }

    setIsPaymentQuoteLoading(true);
    Promise.all([
      listPaidBadgeSets(eventData.id, user.id),
      listPaidBadgeCredits(user.id),
    ])
      .then(([payments, creditPayments]) => {
        if (!isStale) {
          setPaymentQuote(
            calculatePaymentQuote({
              spreadsheetData,
              fieldMapping,
              payments,
              creditPayments,
            })
          );
        }
      })
      .catch((error) => {
        if (!isStale) {
          setPaymentQuote(null);
          toast.error(error.message || "Could not calculate the badge price.");
        }
      })
      .finally(() => {
        if (!isStale) setIsPaymentQuoteLoading(false);
      });

    return () => {
      isStale = true;
    };
  }, [
    eventData?.id,
    importedSpreadsheetData,
    paymentsEnabled,
    printDialogState,
    user?.id,
  ]);

  useEffect(() => {
    hasLoadedEventRef.current = hasLoadedEvent;
    if (hasLoadedEvent && isHydratingEventRef.current) {
      finishEventHydration();
    }
  }, [hasLoadedEvent]);

  useEffect(() => {
    if (
      eventData &&
      hasLoadedEventRef.current &&
      !isHydratingEventRef.current &&
      !loadedEventPayloadSignatureRef.current
    ) {
      captureLoadedEventPayloadSignature();
    }
  }, [eventData]);

  useEffect(() => () => {
    flushPendingEventSave();
    clearTimeout(hydrationSettleTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isTourActive) return;
    setFieldsJoyrideKey((k) => k + 1);
    const id = setTimeout(() => setRunFieldsTour(true), 300);
    return () => clearTimeout(id);
  }, [isTourActive]);

  const handleFieldsTourDismiss = () => {
    markTourSeen();
    setIsTourActive(false);
  };

  const updateRemoveButtonPosition = (selectedElement) => {
    setRemoveButtonPosition({
      top: selectedElement.oCoords.tr.y,
      left: selectedElement.oCoords.tr.x,
    });

    setFieldIdPosition({
      top: selectedElement.oCoords.bl.y,
      left: selectedElement.oCoords.bl.x,
    });

    setElementSizePosition({
      top: selectedElement.oCoords.tl.y - 20,
      left: selectedElement.oCoords.tl.x,
    });
  };

  function removeFieldElement(label) {
    window.canvas.getObjects().forEach((object) => {
      if (object.field && object.id === label) {
        window.canvas.remove(object);
      }
    });
  }

  useEffect(() => {
    const canvas = new fabric.Canvas(canvasRef.current);
    canvas.selection = !readOnly;
    canvas.preserveObjectStacking = true;
    canvas.setHeight(410);
    canvas.setWidth(596);

    window.canvas = canvas;
    setIsCanvasReady(true);

    const scheduleCanvasSave = ({ target } = {}) => {
      if (readOnly && target) {
        target.set({ selectable: false, evented: false });
      }
      if (canvasSideEffectsSuppressedRef.current || target?.skipTemplateSave) {
        return;
      }

      scheduleEventSave();
    };
    const handleObjectRemoved = ({ target } = {}) => {
      if (target?.uploadSpinnerTimer) {
        clearInterval(target.uploadSpinnerTimer);
      }

      if (canvasSideEffectsSuppressedRef.current || target?.skipTemplateSave) {
        return;
      }

      if (!target?.skipAssetDelete) {
        deleteCanvasImageAsset(target);
      }
      scheduleEventSave();
    };
    canvas.on("object:added", scheduleCanvasSave);
    canvas.on("object:modified", scheduleCanvasSave);
    canvas.on("object:removed", handleObjectRemoved);

    const isCanvasTextType = (el) =>
      el &&
      (el.type === "text" ||
        el.type === "i-text" ||
        el.type === "textbox");

    const drawHorizontalLine = (y) => {
      if (window.horizontalLine) return;

      const line = new fabric.Line([0, y, canvas.getWidth(), y], {
        stroke: "red",
        strokeDashArray: [5, 5],
        opacity: 0.5,
        selectable: false,
        evented: false,
      });

      canvas.add(line);
      window.horizontalLine = line;
    };

    const clearHorizontalLine = () => {
      if (window.horizontalLine) {
        canvas.remove(window.horizontalLine);
        setHorizontalLine(null);
        window.horizontalLine = null;
      }
    };

    const drawVerticalLine = (x) => {
      if (window.verticalLine) return;

      const line = new fabric.Line([x, 0, x, canvas.getHeight()], {
        stroke: "red",
        strokeDashArray: [5, 5],
        opacity: 0.5,
        selectable: false,
        evented: false,
      });

      canvas.add(line);
      window.verticalLine = line;
    };

    const clearVerticalLine = () => {
      if (window.verticalLine) {
        canvas.remove(window.verticalLine);
        window.verticalLine = null;
      }
    };

    const clearGuidingLine = () => {
      console.log(window.guidingLines)
      // Remove all existing guiding lines from canvas
      window.guidingLines.forEach(line => {
        canvas.remove(line);
      });

      // Clear the array
      window.guidingLines = [];
    };

    const drawGuidingLine = (x1, y1, x2, y2) => {
      const line = new fabric.Line([x1, y1, x2, y2], {
        stroke: '#b1ccff',
        strokeDashArray: [5, 5],
        opacity: 0.5,
        selectable: false,
        evented: false,
      });

      // Add the line to canvas and to our tracking array
      canvas.add(line);
      window.guidingLines.push(line);

      // Ensure the line is visible above other objects
      line.bringToFront();

      return line;
    };

    const alignElements = (e) => {
      const obj = e.target;
      const objects = canvas
        .getObjects()
        .filter((o) => o !== obj && o.selectable);

      const snapThreshold = 2;

      clearGuidingLine();

      objects.forEach((other) => {
        const objCenterX = obj.left + (obj.width * obj.scaleX) / 2;
        const otherCenterX = other.left + (other.width * other.scaleX) / 2;
        const objCenterY = obj.top + (obj.height * obj.scaleY) / 2;
        const otherCenterY = other.top + (other.height * other.scaleY) / 2;

        // Left edges alignment
        if (Math.abs(obj.left - other.left) < snapThreshold) {
          obj.set("left", other.left);
          drawGuidingLine(other.left, Math.min(other.top, obj.top), other.left,
            Math.max(other.top + other.height * other.scaleY, obj.top + obj.height * obj.scaleY));
        }

        // Right edges alignment
        if (Math.abs((obj.left + obj.width * obj.scaleX) - (other.left + other.width * other.scaleX)) < snapThreshold) {
          obj.set("left", other.left + other.width * other.scaleX - obj.width * obj.scaleX);
          drawGuidingLine(other.left + other.width * other.scaleX, Math.min(other.top, obj.top),
            other.left + other.width * other.scaleX,
            Math.max(other.top + other.height * other.scaleY, obj.top + obj.height * obj.scaleY));
        }

        // Center horizontal alignment (existing)
        if (Math.abs(objCenterX - otherCenterX) < snapThreshold) {
          obj.set(
            "left",
            other.left + (other.width * other.scaleX) / 2 - (obj.width * obj.scaleX) / 2
          );
          drawGuidingLine(otherCenterX, Math.min(other.top, obj.top), otherCenterX,
            Math.max(other.top + other.height * other.scaleY, obj.top + obj.height * obj.scaleY));
        }

        // Top edges alignment
        if (Math.abs(obj.top - other.top) < snapThreshold) {
          obj.set("top", other.top);
          drawGuidingLine(Math.min(other.left, obj.left), other.top,
            Math.max(other.left + other.width * other.scaleX, obj.left + obj.width * obj.scaleX), other.top);
        }

        // Bottom edges alignment
        if (Math.abs((obj.top + obj.height * obj.scaleY) - (other.top + other.height * other.scaleY)) < snapThreshold) {
          obj.set("top", other.top + other.height * other.scaleY - obj.height * obj.scaleY);
          drawGuidingLine(Math.min(other.left, obj.left), other.top + other.height * other.scaleY,
            Math.max(other.left + other.width * other.scaleX, obj.left + obj.width * obj.scaleX),
            other.top + other.height * other.scaleY);
        }

        // Center vertical alignment (existing)
        if (Math.abs(objCenterY - otherCenterY) < snapThreshold) {
          obj.set(
            "top",
            other.top + (other.height * other.scaleY) / 2 - (obj.height * obj.scaleY) / 2
          );
          drawGuidingLine(Math.min(other.left, obj.left), otherCenterY,
            Math.max(other.left + other.width * other.scaleX, obj.left + obj.width * obj.scaleX), otherCenterY);
        }
      });
    };

    const onObjectMoving = (e) => {
      const obj = e.target;
      const canvasCenter = canvas.getWidth() / 2;
      console.log(obj);

      const objectWidth = obj.width * obj.scaleX;
      const objectHeight = obj.height * obj.scaleY;

      const objectCenter = obj.left + objectWidth / 2;
      const canvasCenterY = canvas.getHeight() / 2;
      const objectCenterY = obj.top + objectHeight / 2;
      const threshold = 2;
      const HORIZONTAL_MARGIN = 40
      const VERTICAL_MARGIN = 30

      // Horizontal centering
      if (Math.abs(objectCenter - canvasCenter) < threshold) {
        obj.set("left", canvasCenter - objectWidth / 2);
        drawVerticalLine(canvasCenter);
      } else {
        // Left border alignment
        if (Math.abs(obj.left - HORIZONTAL_MARGIN) < threshold) {
          obj.set("left", HORIZONTAL_MARGIN);
          drawVerticalLine(HORIZONTAL_MARGIN);
        }
        // Right border alignment
        else if (Math.abs((canvas.width - (obj.left + objectWidth)) - HORIZONTAL_MARGIN) < threshold) {
          obj.set("left", canvas.width - objectWidth - HORIZONTAL_MARGIN);
          drawVerticalLine(canvas.width - HORIZONTAL_MARGIN);
        }
        else {
          clearVerticalLine();
        }
      }

      // Vertical centering
      if (Math.abs(objectCenterY - canvasCenterY) < threshold) {
        obj.set("top", canvasCenterY - objectHeight / 2);
        drawHorizontalLine(canvasCenterY);
      } else {
        // Left border alignment
        if (Math.abs(obj.top - VERTICAL_MARGIN) < threshold) {
          obj.set("top", VERTICAL_MARGIN);
          drawHorizontalLine(VERTICAL_MARGIN);
        }
        // Right border alignment
        else if (Math.abs((canvas.height - (obj.top + objectHeight)) - VERTICAL_MARGIN) < threshold) {
          obj.set("top", canvas.height - objectHeight - VERTICAL_MARGIN);
          drawHorizontalLine(canvas.height - VERTICAL_MARGIN);
        }
        else {
          clearHorizontalLine();
        }
      }

      alignElements(e);

      canvas.renderAll();
    };

    // Add event listener to track selected object
    canvas.on("selection:created", (e) => {
      const selectedElement = e.selected[0];
      // selectedElement.bringToFront();
      setSelectedObject({ element: selectedElement });
      updateRemoveButtonPosition(selectedElement);
      setCurrentFont(selectedElement.fontFamily || "");
      if (isCanvasTextType(selectedElement)) {
        setCurrentFontSize(selectedElement.fontSize);
      } else {
        setCurrentFontSize(null);
      }
      console.log(selectedElement);
      setColor(selectedElement.fill);
    });

    canvas.on("selection:updated", (e) => {
      console.log(e.selected[0]);
      const selectedElement = e.selected[0];
      //  selectedElement.bringToFront();
      updateRemoveButtonPosition(selectedElement);
      setCurrentFont(selectedElement.fontFamily || "");
      if (isCanvasTextType(selectedElement)) {
        setCurrentFontSize(selectedElement.fontSize);
      } else {
        setCurrentFontSize(null);
      }
      setColor(selectedElement.fill);
      setSelectedObject({ element: selectedElement });
    });

    canvas.on("object:modified", (e) => {
      const selectedElement = e.target;
      updateRemoveButtonPosition(selectedElement);
      const active = canvas.getActiveObject();
      if (active === selectedElement && isCanvasTextType(active)) {
        setCurrentFontSize(active.fontSize);
      }
      console.log("object:modified");
    });

    canvas.on("object:moving", (e) => {
      const selectedElement = e.target;
      console.log(selectedElement);
      updateRemoveButtonPosition(selectedElement);
      onObjectMoving(e);
    });

    canvas.on("object:scaling", (e) => {
      console.log(e);
      const selectedElement = e.target;
      updateRemoveButtonPosition(selectedElement);

      if (
        selectedElement.type === "text" ||
        selectedElement.type === "textbox"
      ) {
        scaleTextElementFontSize(window.canvas, selectedElement);
        selectedElement.scalingSetFontSize = selectedElement.fontSize;
        setCurrentFontSize(selectedElement.fontSize);
      }
    });

    canvas.on("selection:cleared", () => {
      setSelectedObject({ element: null });
      setCurrentFont("");
      setCurrentFontSize(null);
      setColor(null);
    });

    canvas.on("mouse:up", () => {
      // Align field to the center if it's moved to the center of canvas
      const activeObj = canvas.getActiveObject();
      if (
        activeObj &&
        activeObj.type === 'textbox'
      ) {
        const objectWidth = activeObj.width * activeObj.scaleX;
        const objectCenter = activeObj.left + objectWidth / 2;
        const canvasCenter = canvas.getWidth() / 2;
        const isCentered = Math.abs(objectCenter - canvasCenter) < 2;

        if (isCentered && (activeObj.width - activeObj.calcTextWidth() < 5 || activeObj.textAlign === 'center')) {
          alightTextElementToCenter(activeObj, false);
        }
      }

      clearHorizontalLine();
      clearVerticalLine();
      clearGuidingLine();
    });

    canvas.on("mouse:dblclick", () => {
      if (readOnly) return;
      if (canvas.getActiveObject()?.isLogo) {
        document.getElementById("logoUpload").click();
      }
    });

    return () => {
      canvas.off("object:added", scheduleCanvasSave);
      canvas.off("object:modified", scheduleCanvasSave);
      canvas.off("object:removed", handleObjectRemoved);
      setIsCanvasReady(false);
      canvas.dispose();
    };
  }, [readOnly]);

  useEffect(() => {
    try {
      Object.entries(fields).forEach(([label, field]) => {
        // Only add field element if it's enabled
        if (field.enabled) {
          addFieldElement(label, field);
        }

        const isEmptyCode = (field.type === "qrCode" || field.type === "barcode39" || field.type === "barcode128") && field.value === '';

        if ((!field.enabled || isEmptyCode) && fields[label].element) {
          // Save element properties before removing
          const element = fields[label].element;
          const savedProps = {
            fontSize: element.fontSize,
            fill: element.fill,
            fontWeight: element.fontWeight,
            fontStyle: element.fontStyle,
            fontFamily: element.fontFamily,
            top: element.top,
            left: element.left,
            angle: element.angle,
            height: element.height * (element.scaleY || 1),
            width: element.width * (element.scaleX || 1),
            textAlign: element.textAlign,
          };

          fields[label] = {
            ...fields[label],
            ...savedProps,
            element: null,
          };

          window.canvas.remove(element);
          setSelectedObject({ element: null });
        }
      });
    } finally {
      if (shouldSkipNextFieldsSaveRef.current) {
        shouldSkipNextFieldsSaveRef.current = false;
        canvasSideEffectsSuppressedRef.current =
          previousCanvasSideEffectsSuppressedRef.current;
      }
    }
  }, [fields]);

  const onFieldValueChange = (textElement) => {
    fitTextElementToCanvasWidth(window.canvas, textElement);
  };

  const addFieldElement = (label, field) => {
    if (!field.type || field.type === "text" || field.type === "textbox") {
      if (!field.element) {
        // Create the text element using all parameters from the field object
        field.element = new fabric.Textbox(field.value, {
          ...field, // Spread all properties from the field object
          id: label,
          editable: false, // Ensure the element is not editable initially
          field: true,
        });
        field.element.scalingSetFontSize = field.element.fontSize;

        window.canvas.add(field.element);
        if (field.zIndex !== undefined) {
          window.canvas.moveTo(field.element, field.zIndex);
        }

        window.canvas.renderAll();

        if (field.textAlign === 'center') {
          alightTextElementToCenter(field.element)
        }

        onFieldValueChange(field.element);

        if (newFieldName) {
          window.canvas.setActiveObject(field.element);
        }
      }

      if (field.value !== field.element.text) {
        field.element.set("text", field.value);
        window.canvas.renderAll();
        onFieldValueChange(field.element);
      }

      if (newFieldName) {
        setNewFieldName("");
      }
    } else if (
      field.type === "qrCode" ||
      field.type === "barcode39" ||
      field.type === "barcode128"
    ) {
      if (field.value !== '' && (!field.element || field.value !== field.element.value)) {
        handleQrCodeAdd(
          {
            type: field.type,
            value: field.value,
          },
          (element) => {
            if (!field.element) {
              setElementsCount({
                ...elementsCount,
                qrCode: elementsCount.qrCode + 1,
              });
              // Check field feilds to make this apply only if it's called from applyTemplate
              field.top && element.set({ top: field.top });
              field.left && element.set({ left: field.left });
              field.angle !== undefined && element.set({ angle: field.angle });
              // element.set({ height: field.height });
              // element.set({ width: field.width });
              field.height && element.set({ scaleY: field.height / element.height });
              field.width && element.set({ scaleX: field.width / element.width });
            } else {
              element.set({ top: field.element.top });
              element.set({ left: field.element.left });
              element.set({ angle: field.element.angle });

              if (field.type === "barcode39" ||
                field.type === "barcode128") {
                // element.set({ width: field.element.width });
                element.scaleToWidth(field.element.width * field.element.scaleX);
                element.set({ scaleY: field.element.scaleY });
                element.set({ height: field.element.height });
              } else {
                element.set({ height: field.element.height });
                element.set({ scaleY: field.element.scaleY });
                element.set({ scaleX: field.element.scaleX });
              }
            }

            removeFieldElement(label);

            element.set({ id: label });
            element.field = true;
            element.value = field.value;
            field.element = element;

            if (field.zIndex !== undefined) {
              window.canvas.moveTo(field.element, field.zIndex);
            }

            // we need to do this for the id label to be positioned correctly
            window.canvas.setActiveObject(field.element);
            setSelectedObject({ element: field.element });
            window.canvas.renderAll();
            updateRemoveButtonPosition(field.element);

            // and after we can blur the element
            if (!field.focused) {
              window.canvas.discardActiveObject();
              setSelectedObject({ element: null });
              window.canvas.renderAll();
            }

            if (newFieldName) {
              setNewFieldName("");
            }
          },
          {
            suppressSave:
              isHydratingEventRef.current ||
              shouldSkipNextFieldsSaveRef.current,
          }
        );
      }
    } else {
      throw new Error(`unexpected field type: ${field.type}`);
    }
  };

  const addRectangle = () => {
    const rect = new fabric.Rect({
      left: 100 + elementsCount.rectangle * NEXT_ELEMENT_SHIFT,
      top: 170 - elementsCount.rectangle * NEXT_ELEMENT_SHIFT,
      fill: "orange",
      width: window.canvas.getWidth() - 200,
      height: 30,
      active: true,
    });
    window.canvas.add(rect);
    window.canvas.setActiveObject(rect);
    setElementsCount({
      ...elementsCount,
      rectangle: elementsCount.rectangle + 1,
    });
  };

  const addText = () => {
    const text = new fabric.Textbox("Double click to edit text", {
      left: 50 + elementsCount.text * NEXT_ELEMENT_SHIFT,
      top: 250 - elementsCount.text * NEXT_ELEMENT_SHIFT,
      fontSize: 30,
      width: window.canvas.getWidth() - 100,
      height: 50,
      textAlign: "left",
    });
    window.canvas.add(text);
    window.canvas.setActiveObject(text);
    setElementsCount({
      ...elementsCount,
      text: elementsCount.text + 1,
    });
  };

  const handleQrCodeOpen = () => {
    setOpenQr(true);
  };

  const handleQrCodeClose = () => {
    setOpenQr(false);
  };

  const handleQrCodeAdd = ({ type, value }, onAdd, { suppressSave = false } = {}) => {
    value = String(value ?? "");
    const addCodeImageToCanvas = (img) => {
      const addImage = () => {
        window.canvas.add(img);
        onAdd && onAdd(img);
      };

      if (suppressSave) {
        runWithoutCanvasSideEffects(addImage);
        return;
      }

      addImage();
    };

    if (type === "qrCode") {
      new QRCode(document.getElementById("qrcode"), {
        text: value.replace(/\u00A0/g, ' '),
        onRenderingEnd: (options, qrCodeDataUrl) => {
          fabric.Image.fromURL(qrCodeDataUrl, (img) => {
            img.set({
              left:
                window.canvas.getWidth() -
                120 -
                60 -
                elementsCount.qrCode * NEXT_ELEMENT_SHIFT,
              top: 50 + elementsCount.qrCode * NEXT_ELEMENT_SHIFT,
              scaleY: 120 / img.height,
              scaleX: 120 / img.height,
              codeType: type,
              value,
            });
            img.imageUrl = qrCodeDataUrl;
            addCodeImageToCanvas(img);
          });
        },
      });
    }

    if (type === "barcode39" || type === "barcode128") {
      const barcodeCanvas = document.createElement("canvas");
      JsBarcode(barcodeCanvas, value.replace(/\u00A0/g, ' '), {
        format: type === "barcode39" ? "CODE39" : "CODE128", // You can choose the format you need
        width: 15,
        height: 500,
        displayValue: false,
      });

      fabric.Image.fromURL(barcodeCanvas.toDataURL(), (img) => {
        img.set({
          left:
            window.canvas.getWidth() -
            100 -
            160 -
            elementsCount.qrCode * NEXT_ELEMENT_SHIFT,
          top: 50 + elementsCount.qrCode * NEXT_ELEMENT_SHIFT,
          scaleY: 100 / img.height,
          scaleX: 100 / img.height,
          codeType: type,
          value,
        });
        img.imageUrl = barcodeCanvas.toDataURL();
        addCodeImageToCanvas(img);
      });
    }
  };

  const uploadImageFile = async (file) => {
    if (isDemoMode) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error("Could not read image file."));
        reader.readAsDataURL(file);
      });
    }

    if (!user?.id) {
      throw new Error("You must be signed in to upload images.");
    }

    return uploadEventImage({ file, userId: user.id });
  };

  const loadFabricImage = (imageUrl) =>
    new Promise((resolve, reject) => {
      fabric.Image.fromURL(
        imageUrl,
        (img) => {
          if (!img) {
            reject(new Error("Could not load uploaded image."));
            return;
          }

          resolve(img);
        },
        { crossOrigin: "anonymous" }
      );
    });

  const getCanvasObjectBounds = (object, fallback = {}) => ({
    left: object?.left ?? fallback.left ?? 100,
    top: object?.top ?? fallback.top ?? 100,
    width: object?.getScaledWidth?.() ?? fallback.width ?? 200,
    height: object?.getScaledHeight?.() ?? fallback.height ?? 140,
    zIndex: object ? window.canvas.getObjects().indexOf(object) : fallback.zIndex,
  });

  const addImageUploadPlaceholder = ({ left, top, width, height }) => {
    const spinnerSize = 34;
    const spinnerRadius = spinnerSize / 2;
    const box = new fabric.Rect({
      left: 0,
      top: 0,
      width,
      height,
      fill: "#fff",
      stroke: "#dbe4f0",
      strokeWidth: 1,
      rx: 8,
      ry: 8,
    });
    const spinnerBase = new fabric.Circle({
      left: width / 2,
      top: height / 2,
      originX: "center",
      originY: "center",
      radius: spinnerRadius,
      fill: "transparent",
      stroke: "#dbe4f0",
      strokeWidth: 3,
    });
    const spinnerAccent = new fabric.Circle({
      left: width / 2,
      top: height / 2,
      originX: "center",
      originY: "center",
      radius: spinnerRadius,
      fill: "transparent",
      stroke: "#2667fe",
      strokeWidth: 3,
      strokeDashArray: [27, 160],
    });
    const placeholder = new fabric.Group([box, spinnerBase, spinnerAccent], {
      left,
      top,
      selectable: false,
      evented: false,
      skipTemplateSave: true,
      skipAssetDelete: true,
    });

    placeholder.uploadSpinnerTimer = setInterval(() => {
      spinnerAccent.rotate(((spinnerAccent.angle || 0) + 28) % 360);
      placeholder.dirty = true;
      window.canvas?.requestRenderAll();
    }, 80);

    window.canvas.add(placeholder);
    window.canvas.renderAll();

    return placeholder;
  };

  const removeImageUploadPlaceholder = (placeholder) => {
    if (!placeholder) return;

    clearInterval(placeholder.uploadSpinnerTimer);
    runWithoutCanvasSideEffects(() => {
      window.canvas.remove(placeholder);
    });
  };

  const addImageToCanvas = async (file) => {
    const placement = getCanvasObjectBounds(null, {
      left: 100,
      top: 100,
      width: 200,
      height: 140,
    });
    const placeholder = addImageUploadPlaceholder(placement);
    let imageUrl;

    try {
      imageUrl = await uploadImageFile(file);
      const img = await loadFabricImage(imageUrl);
      img.set({
        left: placement.left,
        top: placement.top,
        angle: 0,
        padding: 10,
        cornersize: 10,
        imageUrl,
        eventAssetPath: parseEventAssetPath(imageUrl),
      });
      img.scaleToWidth(placement.width);
      removeImageUploadPlaceholder(placeholder);
      window.canvas.add(img).setActiveObject(img);
      window.canvas.renderAll();
      scheduleEventSave();
    } catch (error) {
      removeImageUploadPlaceholder(placeholder);
      if (imageUrl) {
        deleteEventImage({ imageUrl }).catch(() => { });
      }
      toast.error(error.message || "Could not upload image.");
    }
  };

  const handleImageAdd = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (file) {
      await addImageToCanvas(file); // Upload image and add public URL to the canvas
      setOpenImage(false);
    }
  };

  const handleLogoAdd = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;

    const logoElement = window.logoElement;
    if (!logoElement || logoElement.type !== "image") {
      toast.error("Select a logo placeholder before uploading a logo.");
      return;
    }

    const logoBounds = getCanvasObjectBounds(logoElement);
    const placeholder = addImageUploadPlaceholder(logoBounds);
    let imageUrl;

    try {
      imageUrl = await uploadImageFile(file);
      const newImage = await loadFabricImage(imageUrl);
      const scaleFactor = logoBounds.height / newImage.height;
      newImage.scale(scaleFactor);
      newImage.set({
        left: logoBounds.left + (logoBounds.width - newImage.getScaledWidth()) / 2,
        top: logoBounds.top,
        angle: logoElement.angle || 0,
        padding: 10,
        cornersize: 10,
        imageUrl,
        eventAssetPath: parseEventAssetPath(imageUrl),
      });

      removeImageUploadPlaceholder(placeholder);
      runWithoutCanvasSideEffects(() => {
        if (window.canvas.getObjects().includes(logoElement)) {
          window.canvas.remove(logoElement);
        }
      });
      deleteCanvasImageAsset(logoElement);
      window.logoElement = null;
      window.canvas.add(newImage).setActiveObject(newImage);
      if (logoBounds.zIndex !== undefined && logoBounds.zIndex >= 0) {
        window.canvas.moveTo(newImage, logoBounds.zIndex);
      }
      window.canvas.renderAll();
      scheduleEventSave();
    } catch (error) {
      removeImageUploadPlaceholder(placeholder);
      if (imageUrl) {
        deleteEventImage({ imageUrl }).catch(() => { });
      }
      toast.error(error.message || "Could not upload logo.");
    } finally {
      setOpenImage(false);
    }
  };

  const printPDF = () => {
    const canvas = window.canvas;
    const dataURL = canvas.toDataURL({
      width: canvas.width,
      height: canvas.height,
      left: 0,
      top: 0,
      format: "png",
      multiplier: 10,
    });

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: "A4",
    });

    const badgeWidth = canvas.width / 3;
    const badgeHeight = canvas.height / 3;

    pdf.addImage(dataURL, "PNG", 20, 20, badgeWidth, badgeHeight);
    pdf.addImage(
      dataURL,
      "PNG",
      20 + badgeWidth,
      20 + badgeHeight,
      badgeWidth,
      badgeHeight
    );
    pdf.save("badge.pdf");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: "array" });

        // Assuming we're interested in the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert sheet data to JSON format
        let valuesItems = XLSX.utils
          .sheet_to_json(worksheet, { header: 1 })
          .filter((values) => values.length);

        valuesItems.splice(0, 1);

        debugger;

        //generatePDF(text);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const onFieldRemove = (label) => {
    const newFields = { ...fields };

    window.canvas.remove(fields[label].element);
    setSelectedObject({ element: null });

    delete newFields[label];

    setFields(newFields);
  };

  const onFieldFocus = (label) => {
    const canvas = window.canvas;
    const element = fields[label]?.element;
    const isCanvasElement = element && canvas.getObjects().includes(element);

    if (!isCanvasElement) {
      canvas.discardActiveObject();
      setSelectedObject({ element: null });
      canvas.renderAll();
      return;
    }

    canvas.setActiveObject(element);
    setSelectedObject({ element });
    canvas.renderAll();
  };

  const onFieldBlur = () => {
    //window.canvas.setActiveObject(null);
    //setSelectedObject({ element: null });
  };

  const A4_WIDTH_MM = 297;
  const A4_HEIGHT_MM = 210;
  const PRINT_GAP_MM = 20;

  const setFieldValue = async (label, field, value, isInitialValue) => {
    const canvas = window.canvas;
    // Spreadsheet cells may be numbers or booleans, even when preview inputs
    // already display them as strings.
    value = normalizeTemplateFieldValue(value);

    return new Promise((resolve) => {
      if (!field.type || field.type === "text" || field.type === "textbox") {
        field.element.set("text", value);
        canvas.renderAll();
        onFieldValueChange(field.element);
        resolve();
      } else if (
        field.type === "qrCode" ||
        field.type === "barcode39" ||
        field.type === "barcode128"
      ) {
        if (value === '') {
          const element = field.element;
          if (element) {
            const savedProps = {
              fontSize: element.fontSize,
              fill: element.fill,
              fontWeight: element.fontWeight,
              fontStyle: element.fontStyle,
              fontFamily: element.fontFamily,
              top: element.top,
              left: element.left,
              height: element.height * (element.scaleY || 1),
              width: element.width * (element.scaleX || 1),
              textAlign: element.textAlign,
            };
            Object.assign(field, savedProps);
          }

          removeFieldElement(label);
          field.element = null;
          resolve();
          return;
        }

        handleQrCodeAdd(
          {
            type: field.type,
            value,
          },
          (img) => {
            if (!field.element) {
              field.top && img.set({ top: field.top });
              field.left && img.set({ left: field.left });
              field.height && img.set({ scaleY: field.height / img.height });
              field.width && img.set({ scaleX: field.width / img.width });
            } else {
              img.set({ top: field.element.top });
              img.set({ left: field.element.left });

              if (field.type === "barcode39" ||
                field.type === "barcode128") {
                img.scaleToWidth(field.element.width * field.element.scaleX);
                img.set({ scaleY: field.element.scaleY });
                img.set({ height: field.element.height });
              } else {
                img.set({ height: field.element.height });
                img.set({ scaleY: field.element.scaleY });
                img.set({ scaleX: field.element.scaleX });
              }
            }

            removeFieldElement(label);

            img.set({ id: label });
            img.field = true;
            field.element = img;

            if (field.zIndex !== undefined) {
              canvas.moveTo(field.element, field.zIndex);
            }

            if (isInitialValue && field.element) {
              // we need to do this for the id label to be positioned correctly
              canvas.setActiveObject(field.element);
              setSelectedObject({ element: field.element });
              canvas.renderAll();
              updateRemoveButtonPosition(field.element);

              // and after we can blur the element
              canvas.discardActiveObject();
              setSelectedObject({ element: null });
            }

            canvas.renderAll();

            resolve();
          },
          { suppressSave: isHydratingEventRef.current }
        );
      } else {
        throw new Error(`unexpected field type: ${field.type}`);
      }
    });
  };

  const processAttendees = async (attendees, index = 0) => {
    const canvas = window.canvas;

    if (index >= attendees.length) {
      return []; // Base case: return an empty array when all attendees are processed
    }

    const attendee = attendees[index];

    // Create an array of promises for setting field values
    const fieldPromises = Object.entries(fields)
      .filter(([label, field]) => field.enabled)
      .map(async ([label, field]) => {
        await setFieldValue(label, field, attendee[label] ?? "");
        // element.set("text", attendee[label].replace(/ /g, "\u00A0"));
        // canvas.renderAll();
        // onFieldValueChange(element);
      });

    // Wait for all field value promises to resolve
    await Promise.all(fieldPromises);

    // Get the data URL after all fields are set for the current attendee
    const dataURL = canvas.toDataURL({
      width: canvas.width,
      height: canvas.height,
      left: 0,
      top: 0,
      format: "image/jpeg",
      multiplier: 2,
    });

    // Recursively process the next attendee
    const remainingDataURLs = await processAttendees(attendees, index + 1);

    // Return the current data URL along with the remaining data URLs
    return [dataURL, ...remainingDataURLs];
  };

  const getCurrentFieldValues = () => Object.entries(fields).reduce(
    (acc, [label, { value }]) => {
      acc[label] = value;
      return acc;
    },
    {}
  );

  const restoreFieldValues = async (values) => {
    const fieldPromises = Object.entries(fields)
      .filter(([label, field]) => field.enabled).map(
        async ([label, field]) => {
          await setFieldValue(label, field, values[label] ?? "", true);
        }
      );

    await Promise.all(fieldPromises);
    window.canvas.renderAll();
  };

  const generatePreviewPDFViaWorker = async (attendees, format) => {
    const worker = new Worker(new URL("../worker.js", import.meta.url));
    const initialValues = getCurrentFieldValues();

    try {
      const dataURLs = await processAttendees(attendees);

      return await new Promise((resolve, reject) => {
        worker.onmessage = (event) => {
          if (event.data.type === "preview_pdf") {
            resolve(event.data.pdfOutput);
            worker.terminate();
            return;
          }

          worker.terminate();
          reject(new Error("unexpected web worker message type"));
        };

        worker.onerror = (error) => {
          worker.terminate();
          reject(new Error(error.message || "preview web worker failed"));
        };

        worker.postMessage({
          mode: "generate_preview_pdf",
          dataURLs,
          size,
          format
        });
      });
    } finally {
      await restoreFieldValues(initialValues);
    }
  };

  const generateMultipleBadgesPDFViaWorker = async (attendees, format) => {
    const initialValues = getCurrentFieldValues();
    let worker = null;

    try {
      const dataURLs = await processAttendees(attendees);
      worker = new Worker(new URL("../worker.js", import.meta.url));

      await new Promise((resolve, reject) => {
        const fail = (error) => {
          worker.terminate();
          reject(error);
        };

        worker.onmessage = (event) => {
          try {
            switch (event.data.type) {
              case "save_pdf":
                saveAs(event.data.pdfOutput, event.data.name);
                break;

              case "done":
                worker.terminate();
                resolve();
                break;

              default:
                fail(new Error("unexpected web worker message type"));
            }
          } catch (error) {
            fail(error);
          }
        };

        worker.onerror = (error) => {
          fail(new Error(error.message || "badge generation failed"));
        };

        worker.postMessage({
          dataURLs,
          size,
          format
        });
      });
    } finally {
      worker?.terminate();
      await restoreFieldValues(initialValues);
    }

    setPrintDialogState({ type: 'closed' });
    setIsSuccessDialogOpen(true);
  };

  const generateBadgesAfterPurchase = async ({ attendees, format }) => {
    const spreadsheetData =
      printDialogState.purchaseSpreadsheetData ||
      importedSpreadsheetDataRef.current;
    const fieldMapping =
      printDialogState.purchaseFieldMapping || spreadsheetData?.mapping || {};

    if (!spreadsheetData || !eventData?.id || !session?.access_token) {
      throw new Error("Payment is only available for a saved event with attendees.");
    }

    flushPendingEventSave();
    const purchase = await beginBadgePurchase({
      eventId: eventData.id,
      fieldMapping,
      spreadsheetData,
    });

    if (purchase.requiresPayment) {
      storePendingGeneration(purchase.sessionId, { format });
      window.location.assign(purchase.checkoutUrl);
      return;
    }

    setHasPurchasedBadgeSets(true);

    if (
      purchase.quote?.unmatchedAttendeeCount === 0 ||
      purchase.quote?.freeAttendeeIndexes?.length > 0 ||
      purchase.quote?.badgeCreditUsedCount > 0
    ) {
      await generateMultipleBadgesPDFViaWorker(attendees, format);
      return;
    }

    setPendingPaidGeneration({ attendees, format });
    setSuccessfulPaymentSummary({
      amountCents: purchase.amountCents || 0,
      badgeCreditPurchasedCount:
        purchase.quote?.badgeCreditPurchasedCount || 0,
      badgeCreditUsedCount: purchase.quote?.badgeCreditUsedCount || 0,
    });
    setPrintDialogState({ type: "closed" });
    setIsPaymentSuccessDialogOpen(true);
  };

  const openPurchasedBadgePreview = (payment, attendees) => {
    const purchasedFieldMapping = getPurchasedFieldMapping(payment);
    setPrintDialogState({
      type: "attendees",
      attendees,
      purchaseSpreadsheetData: payment.spreadsheetData,
      purchaseFieldMapping: purchasedFieldMapping,
    });
  };

  const openPurchasedBadgeSetPreview = (payment) => {
    const purchasedFieldMapping = getPurchasedFieldMapping(payment);
    const attendees = spreadsheetDataToAttendees(
      payment.spreadsheetData,
      purchasedFieldMapping
    );
    const compatibility = getRegenerationCompatibility(fields, payment);

    setIsPurchasedBadgesDialogOpen(false);

    if (compatibility.requiresVisibleSharedField) {
      setHiddenPurchasedFieldNames(compatibility.sharedFieldNames);
      return;
    }

    if (compatibility.requiresFieldMapping) {
      setRegenerationMappingState({
        attendees,
        currentFieldNames: compatibility.visibleCurrentFieldNames,
        payment,
        purchasedFieldNames: compatibility.purchasedFieldNames,
      });
      return;
    }

    openPurchasedBadgePreview(payment, attendees);
  };

  const handlePurchasedFieldsMapping = (mapping) => {
    if (!regenerationMappingState) return;

    const { attendees, payment } = regenerationMappingState;
    const mappedAttendees = mapPurchasedAttendeesToCurrentFields(
      attendees,
      mapping
    );
    setRegenerationMappingState(null);
    openPurchasedBadgePreview(payment, mappedAttendees);
  };

  const handleColorChange = (color) => {
    if (!color || !selectedObject.element) {
      return;
    }

    selectedObject.element.set("fill", color);
    setColor(color);
    window.canvas.renderAll();
    setSelectedObject({ ...selectedObject });
    scheduleEventSave();
  };

  const toggleBold = () => {
    if (
      selectedObject.element &&
      (selectedObject.element.type === "text" ||
        selectedObject.element.type === "textbox")
    ) {
      const isBold = selectedObject.element.fontWeight === "bold";
      selectedObject.element.set("fontWeight", isBold ? "normal" : "bold");
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  };

  const toggleItalic = () => {
    if (
      selectedObject.element &&
      (selectedObject.element.type === "text" ||
        selectedObject.element.type === "textbox")
    ) {
      const isItalic = selectedObject.element.fontStyle === "italic";
      selectedObject.element.set("fontStyle", isItalic ? "normal" : "italic");
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  };

  const logTemplate = () => {
    console.info('Template: ', extractBadgeTemplate(window.canvas, fields));
  };

  const applyTemplate = async (
    template,
    size,
    importedSpreadsheetData,
    { suppressSave = false, existingFields = null } = {}
  ) => {
    const canvas = window.canvas;
    const wasSuppressed = canvasSideEffectsSuppressedRef.current;

    if (suppressSave) {
      canvasSideEffectsSuppressedRef.current = true;
    }

    try {
      const canvasSize = calculateCanvasSize(size)

      runWithoutCanvasSideEffects(() => {
        canvas.clear();
        window.logoElement = null;
        window.horizontalLine = null;
        window.verticalLine = null;
        window.guidingLine = null;
      });
      canvas.setHeight(canvasSize.height);
      canvas.setWidth(canvasSize.width);

      canvas.renderAll();

      runWithoutCanvasSideEffects(() => {
        Object.values(fields).forEach(({ element }) => {
          canvas.remove(element);
        });
      });

      setFields(
        getFieldsStateFromTemplateFields(
          existingFields
            ? getTemplateFieldsForExistingEventFields(
              template,
              importedSpreadsheetData,
              existingFields
            )
            : getTemplateFieldsForImportedSpreadsheetData(
              template,
              importedSpreadsheetData
            )
        )
      );

      if (template.isBlankTemplate) {
        if (!hasSeenTour()) {
          setIsTourActive(true);
        }
        return;
      }

      await renderBadgeTemplateObjects(canvas, template.objects, {
        alignCenteredText: alightTextElementToCenter,
        getImageAssetPath: parseEventAssetPath,
        onLogoElement: (img) => {
          window.logoElement = img;
        },
      });
      if (!hasSeenTour()) {
        setIsTourActive(true);
      }
    } finally {
      if (suppressSave) {
        canvasSideEffectsSuppressedRef.current = wasSuppressed;
      }
    }
  };

  const handleBadgeSizeChange = async (selectedSize) => {
    const currentSize = sizeRef.current;

    setIsChangeSizeDialogOpen(false);

    if (!selectedSize || !currentSize || !window.canvas) {
      setSize(selectedSize);
      return;
    }

    const currentTemplate = extractBadgeTemplate(
      window.canvas,
      fieldsRef.current
    );
    const resizedTemplate = calculateTemplateForSize(
      currentTemplate,
      currentSize,
      selectedSize
    );

    await applyTemplate(
      resizedTemplate,
      selectedSize,
      importedSpreadsheetDataRef.current
    );
    setSize(selectedSize);
    setSelectedObject({ element: null });
    trackEvent({
      action: "change_badge_size",
      data: {
        selectedBadgeSize: getBadgeSizeTrackingValue(selectedSize),
      },
    });
  };

  const openChangeSizeDialog = () => {
    trackEvent({
      action: "click_change_badge_size",
    });
    setIsChangeSizeDialogOpen(true);
  };

  const openReimportDialog = () => {
    trackEvent({
      action: "click_reimport_attendees",
    });
    setIsReimportDialogOpen(true);
  };

  const openChangeTemplateModal = () => {
    trackEvent({
      action: "click_change_badge_template",
    });
    setIsChangeTemplateMode(true);
    setIsModalOpen(true);
  };

  const closeChangeTemplateModal = () => {
    setIsModalOpen(false);
    setIsChangeTemplateMode(false);
  };

  useEffect(() => {
    if (!eventData?.badgeTemplate || !eventData?.badgeSize || !isCanvasReady || hasLoadedEvent) {
      return undefined;
    }

    let isCancelled = false;

    const loadEventTemplate = async () => {
      isHydratingEventRef.current = true;

      try {
        const eventSpreadsheetData = buildImportedSpreadsheetDataFromEvent(eventData);
        setImportedSpreadsheetDataState(eventSpreadsheetData);
        importedSpreadsheetDataRef.current = eventSpreadsheetData;
        setSize(eventData.badgeSize);
        await applyTemplate(eventData.badgeTemplate, eventData.badgeSize, eventSpreadsheetData, {
          suppressSave: true,
        });

        if (!isCancelled) {
          setIsModalOpen(false);
          setHasLoadedEvent(true);
        } else {
          isHydratingEventRef.current = false;
        }
      } catch (loadError) {
        isHydratingEventRef.current = false;
        console.error("Could not apply event template.", loadError);
      }
    };

    loadEventTemplate();

    return () => {
      isCancelled = true;
    };
  }, [eventData, isCanvasReady, hasLoadedEvent]);

  const changeFont = (font) => {
    if (
      selectedObject.element &&
      (selectedObject.element.type === "text" ||
        selectedObject.element.type === "textbox")
    ) {
      selectedObject.element.set({ fontFamily: font });
      setCurrentFont(font);
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  };

  const changeFontSize = (size) => {
    const el = selectedObject.element;
    if (
      !el ||
      (el.type !== "text" &&
        el.type !== "i-text" &&
        el.type !== "textbox")
    ) {
      return;
    }
    const n = Number(size);
    if (!Number.isFinite(n) || n <= 0) return;
    el.set({ fontSize: n });
    el.scalingSetFontSize = n;
    setCurrentFontSize(n);
    window.canvas.renderAll();
    setSelectedObject({ ...selectedObject });
    scheduleEventSave();
  };

  const alignTextLeft = () => {
    if (
      (selectedObject.element && selectedObject.element.type === "text") ||
      selectedObject.element.type === "textbox"
    ) {
      selectedObject.element.set({ textAlign: "left" });
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  };

  const alightTextElementToCenter = (element, shouldRenderCanvas = true) => {
    // Store the current height and scale
    const currentHeight = element.height * element.scaleY;

    // Set text alignment to center
    element.set({
      textAlign: "center",
      left: 40,
      width: window.canvas.width - 80
    });

    // Reset scale and maintain the original height
    element.set({
      scaleX: 1,
      scaleY: currentHeight / element.height
    });

    if (shouldRenderCanvas) {
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  }

  const alignTextCenter = () => {
    if (
      (selectedObject.element && selectedObject.element.type === "text") ||
      selectedObject.element.type === "textbox"
    ) {
      alightTextElementToCenter(selectedObject.element)
    }
  };

  const alignTextRight = () => {
    if (
      (selectedObject.element && selectedObject.element.type === "text") ||
      selectedObject.element.type === "textbox"
    ) {
      selectedObject.element.set({ textAlign: "right" });
      window.canvas.renderAll();
      setSelectedObject({ ...selectedObject });
      scheduleEventSave();
    }
  };

  const updateSelectedObjectLayer = (moveSelectedObject) => {
    const element = selectedObject.element;
    if (!element) {
      return;
    }

    moveSelectedObject(element);
    window.canvas.renderAll();
    setSelectedObject({ element });
    updateRemoveButtonPosition(element);
    scheduleEventSave();
  };

  const bringSelectedForward = () => {
    updateSelectedObjectLayer((element) => element.bringForward());
  };

  const sendSelectedBackward = () => {
    updateSelectedObjectLayer((element) => element.sendBackwards());
  };

  const removeActiveElement = () => {
    if (selectedObject.element) {
      window.canvas.remove(selectedObject.element);
      setSelectedObject({ element: null });
      setCurrentFont("");
      setCurrentFontSize(null);
      setColor(null);
      scheduleEventSave();
    }
  };

  const onAddLogo = () => {
    fabric.Image.fromURL(LOGO_IMAGE_DATA_URL, (img) => {
      img.set({ left: 100, top: 100, isLogo: true });
      img.scaleToWidth(200);
      window.logoElement = img;
      window.canvas.add(img).setActiveObject(img);
    });
  };

  const headerTitle =
    headerProps.title ||
    (mode === "create-event" ? importedSpreadsheetData?.eventName || draftEventName : eventData?.name);

  return (
    <div className="app-container">
      <Header
        isDemoMode={isDemoMode}
        {...headerProps}
        onBackClick={flushPendingEventSave}
        title={headerTitle}
      />
      <div className="wrapper">
        <div className="badge-editor">
          <Joyride
            key={fieldsJoyrideKey}
            steps={tourSteps}
            run={runFieldsTour}
            continuous
            showProgress
            showSkipButton
            locale={{ skip: "Skip tour", last: "Finish tour" }}
            disableScrolling={false}
            callback={(data) => {
              if (data.type === EVENTS.TOOLTIP) {
                queueMicrotask(() => {
                  document
                    .querySelector(
                      ".react-joyride__tooltip button[data-action='primary']"
                    )
                    ?.classList.add("primary");
                });
              }
              if ([STATUS.FINISHED, STATUS.SKIPPED].includes(data.status)) {
                setRunFieldsTour(false);
                handleFieldsTourDismiss();
              }
            }}
            styles={{
              options: {
                zIndex: 10000,
                primaryColor: "rgb(38, 103, 254)",
              },
            }}
          />
          <fieldset className="editor-read-only-section" disabled={readOnly}>
          <Controls
            addRectangle={addRectangle}
            addText={addText}
            handleImageOpen={() =>
              document.getElementById("imageUpload").click()
            }
            onAddLogo={onAddLogo}
            handleQrCodeOpen={handleQrCodeOpen}
            logTemplate={logTemplate}
            size={size}
            onChangeSizeClick={openChangeSizeDialog}
          />
          </fieldset>
          <div data-tour="badge-editor-canvas-column">
            <fieldset className="editor-read-only-section" disabled={readOnly}>
            <TopControls
              selectedObject={selectedObject}
              toggleBold={toggleBold}
              toggleItalic={toggleItalic}
              currentFont={currentFont}
              currentFontSize={currentFontSize}
              applyColor={handleColorChange}
              color={color}
              changeFont={changeFont}
              changeFontSize={changeFontSize}
              alignTextLeft={alignTextLeft}
              alignTextCenter={alignTextCenter}
              alignTextRight={alignTextRight}
              bringSelectedForward={bringSelectedForward}
              sendSelectedBackward={sendSelectedBackward}
            />
            </fieldset>
            <div className="canvas-wrapper">
              <div className="canvas-wrapper-relative">
                <Canvas canvasRef={canvasRef} />
                {selectedObject.element && !selectedObject.element.field && (
                  <div
                    className="remove-btn"
                    style={{
                      position: "absolute",
                      top: `${removeButtonPosition.top}px`,
                      left: `${removeButtonPosition.left}px`,
                    }}
                    onClick={readOnly ? undefined : removeActiveElement}
                  >
                    &times;
                  </div>
                )}

                {selectedObject.element &&
                  (selectedObject.element.field ||
                    selectedObject.element.isLogo) && (
                    <div
                      className="field-id"
                      style={{
                        top: `${fieldIdPosition.top}px`,
                        left: `${fieldIdPosition.left}px`,
                      }}
                    >
                      {selectedObject.element.isLogo
                        ? "Double-click to add a logo"
                        : selectedObject.element.id}
                    </div>
                  )}

                {selectedObject.element && (
                  <div
                    className="element-size"
                    style={{
                      top: `${elementSizePosition.top}px`,
                      left: `${elementSizePosition.left}px`,
                    }}
                  >
                    {`${Math.ceil(selectedObject.element.height * selectedObject.element.scaleY)} x ${Math.ceil(selectedObject.element.width * selectedObject.element.scaleX)}`}
                  </div>
                )}
              </div>
            </div>
            {size && (
              <div className="canvas-actions">
                <button
                  className="button link-button change-template-button"
                  type="button"
                  onClick={openChangeTemplateModal}
                  disabled={readOnly}
                >
                  Change template
                </button>
              </div>
            )}
          </div>
          <PDFControls
            fields={fields}
            setFields={setFields}
            updateFields={updateFields}
            printSamplePDF={() => setPrintDialogState({
              type: 'sample', sampleAttendee: Object.entries(fields).reduce(
                (acc, [label, { value }]) => {
                  acc[label] = value;
                  return acc;
                },
                {}
              )
            })}
            printStaticPDF={() => setPrintDialogState({
              type: 'static'
            })}
            handleFileSelect={handleFileSelect}
            onPrintMultipleBadgesClick={() => {
              if (importedSpreadsheetData) {
                const attendees = spreadsheetDataToAttendees(importedSpreadsheetData);

                setPrintDialogState({ type: 'attendees', attendees });
              } else {
                setPrintDialogState({ type: 'static' });
              }
            }}
            onFieldRemove={onFieldRemove}
            onFieldFocus={onFieldFocus}
            onFieldBlur={onFieldBlur}
            onFieldAdd={() => setIsNewFieldDialogOpen(true)}
            isTemplateModalOpen={isModalOpen}
            importedSpreadsheetData={importedSpreadsheetData}
            setImportedSpreadsheetData={setImportedSpreadsheetData}
            onReimportClick={
              isDemoMode ? undefined : openReimportDialog
            }
            attendeeSelectionResetKey={attendeeSelectionResetKey}
            elementsCount={elementsCount}
            setElementsCount={setElementsCount}
            setNewFieldName={setNewFieldName}
            paymentsEnabled={paymentsEnabled}
            showPurchasedBadgesButton={hasPurchasedBadgeSets}
            onPurchasedBadgesClick={() => setIsPurchasedBadgesDialogOpen(true)}
            readOnly={readOnly}
          />
          <ImportSpreadsheetDialog
            open={isReimportDialogOpen}
            variant="reimport"
            existingFieldOptions={existingImportFieldOptions}
            onClose={() => setIsReimportDialogOpen(false)}
            onContinue={handleReimportContinue}
          />
          <SizeDialog
            open={isChangeSizeDialogOpen}
            useInitialOverlay={false}
            onClose={() => setIsChangeSizeDialogOpen(false)}
            onContinue={handleBadgeSizeChange}
          />
          {printDialogState.type !== 'closed' &&
            <PrintDialog
              state={printDialogState}
              size={size}
              onClose={() => setPrintDialogState({ type: 'closed' })}
              onContinue={({ attendees, format }) =>
                paymentsEnabled
                  ? generateBadgesAfterPurchase({ attendees, format })
                  : generateMultipleBadgesPDFViaWorker(attendees, format)
              }
              onPreview={({ attendees, format }) => generatePreviewPDFViaWorker(attendees, format)}
              paymentEnabled={paymentsEnabled}
              priceQuote={paymentQuote}
              isPriceLoading={
                paymentsEnabled && (isPaymentQuoteLoading || !paymentQuote)
              }
            />
          }
          {paymentsEnabled && (
            <>
              <PurchasedBadgesDialog
                eventId={eventData.id}
                userId={user.id}
                open={isPurchasedBadgesDialogOpen}
                onClose={() => setIsPurchasedBadgesDialogOpen(false)}
                onRegenerate={openPurchasedBadgeSetPreview}
              />
              <HiddenPurchasedFieldsDialog
                fieldNames={hiddenPurchasedFieldNames}
                open={hiddenPurchasedFieldNames.length > 0}
                onClose={() => setHiddenPurchasedFieldNames([])}
              />
              <PurchasedFieldsMappingDialog
                attendees={regenerationMappingState?.attendees || []}
                currentFieldNames={
                  regenerationMappingState?.currentFieldNames || []
                }
                open={Boolean(regenerationMappingState)}
                purchasedFieldNames={
                  regenerationMappingState?.purchasedFieldNames || []
                }
                onClose={() => setRegenerationMappingState(null)}
                onSubmit={handlePurchasedFieldsMapping}
              />
            </>
          )}
          <SuccessfulPaymentDialog
            open={isPaymentSuccessDialogOpen}
            {...successfulPaymentSummary}
            canGenerate={isCanvasReady && hasLoadedEvent}
            onClose={() => {
              setIsPaymentSuccessDialogOpen(false);
              setPendingPaidGeneration(null);
            }}
            onContinue={async () => {
              const generation = pendingPaidGeneration;
              if (!generation) {
                throw new Error("Badge generation details are no longer available.");
              }

              await generateMultipleBadgesPDFViaWorker(
                generation.attendees,
                generation.format
              );
              setIsPaymentSuccessDialogOpen(false);
              setPendingPaidGeneration(null);
            }}
          />
          <SuccessDialog
            open={isSuccessDialogOpen}
            onClose={() => setIsSuccessDialogOpen(false)}
            onContinue={() => {
              setIsSuccessDialogOpen(false);
              openHelpImproveDialogIfNeeded();
            }}
          />
          <QRCodeDialog
            open={openQr}
            handleClose={handleQrCodeClose}
            handleAdd={(params) => {
              handleQrCodeAdd(params, () => {
                setElementsCount({
                  ...elementsCount,
                  qrCode: elementsCount.qrCode + 1,
                });
                setOpenQr(false);
              });
            }}
          />
          <NewFieldDialog
            fields={fields}
            open={isNewFieldDialogOpen}
            handleClose={() => {
              setIsNewFieldDialogOpen(false);
              setNewFieldName("");
            }}
            handleAdd={({ value, type }) => {
              setNewFieldName(value);
              setFields({
                ...fields,
                [value]: {
                  value,
                  type,
                  left: 130 + elementsCount.text * NEXT_ELEMENT_SHIFT,
                  top: 270 - elementsCount.text * NEXT_ELEMENT_SHIFT,
                  fontSize: 50,
                  fontWeight: "bold",
                  fontFamily: "Josefin Sans",
                  element: null,
                },
              });

              setElementsCount({
                ...elementsCount,
                text: elementsCount.text + 1,
              });
              setIsNewFieldDialogOpen(false);
            }}
          />
          <SuccessDialog
            open={isStaticSuccessDialogOpen}
            isSample={!!Object.entries(fields).length}
            onClose={() => setIsStaticSuccessDialogOpen(false)}
            onContinue={() => {
              setIsStaticSuccessDialogOpen(false);
              openHelpImproveDialogIfNeeded();
            }}
          />
          <HelpImproveDialog
            open={isHelpImproveOpen}
            onClose={() => setIsHelpImproveOpen(false)}
          />
          <div style={{ display: "none" }}>
            <div id="qrcode" />
          </div>
        </div>
        <Modal
          open={isModalOpen}
          className={isChangeTemplateMode ? "change-template-modal-root" : undefined}
          disableEnforceFocus
          onClose={isChangeTemplateMode ? closeChangeTemplateModal : undefined}
          BackdropProps={
            isChangeTemplateMode
              ? { className: "template-modal-overlay-backdrop" }
              : { className: "template-modal-backdrop" }
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            ...(isChangeTemplateMode ? {} : { backgroundColor: "#e6ebef" }),
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
              padding: "20px 0",
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "90%",
              height: "90%",
              overflowY: "scroll",
              boxShadow: "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
            }}
          >
            <TemplateCards
              isDemoMode={isDemoMode}
              skipImportStep={isChangeTemplateMode}
              initialSize={isChangeTemplateMode ? size : null}
              excludedEventId={isChangeTemplateMode ? eventData?.id : null}
              onCancel={isChangeTemplateMode ? closeChangeTemplateModal : undefined}
              onTemplateSelect={async (template, size) => {
                const eventSpreadsheetData = importedSpreadsheetDataRef.current;

                await applyTemplate(
                  template,
                  size,
                  eventSpreadsheetData,
                  isChangeTemplateMode
                    ? { existingFields: fieldsRef.current }
                    : undefined
                );
                setIsModalOpen(false);
                setIsChangeTemplateMode(false);
                setSelectedObject({ element: null });
                setSize(size);
                if (isChangeTemplateMode) {
                  trackEvent({
                    action: "change_badge_template",
                  });
                }

                if (!isChangeTemplateMode && !isDemoMode && mode === "create-event" && onEventCreate) {
                  await onEventCreate({
                    badgeSize: size,
                    badgeTemplate: getTemplateForImportedSpreadsheetData(
                      template,
                      eventSpreadsheetData
                    ),
                    importedSpreadsheetData: eventSpreadsheetData,
                  });
                }
              }}
              setImportedSpreadsheetData={setImportedSpreadsheetData}
              onDraftEventNameChange={setDraftEventName}
              onEventNameValidate={onEventNameValidate}
              importedSpreadsheetData={importedSpreadsheetData}
            />
          </div>
        </Modal>
      </div>
      <Footer />
      {/* Hidden file input */}
      <input
        type="file"
        id="imageUpload"
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleImageAdd}
      />
      <input
        type="file"
        id="logoUpload"
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleLogoAdd}
      />
    </div >
  );
};

export default BadgeEditor;
